import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Stack } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';

import useIdbSync from 'src/services/idbSync/useIdbSync';
import { ExamStepEnum } from 'src/services/offline/db';
import { setOrderLastStepIDB } from 'src/services/offline/outboxClient';
import {
  captureExamEvent,
  captureExamException,
  EXAM_EVENTS,
  EXAM_STEP,
  markAnalysisReceived,
  secondsSince,
} from 'src/services/posthogEvents';
import Sentry from 'src/services/sentry';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import {
  CustomModal,
  CustomModalBody,
  CustomModalHeader,
  CustomModalTitle,
} from 'src/components/custom_components/CustomModal';

import { BackendStatusEnum } from 'src/models/GalenData/DeviceDataView.model';
import { I18NextNS } from 'src/models/Language.model';
import { EyesPosition } from 'src/models/Session.model';

import useActionAnalyzeModel from 'src/hooks/useActionAnalyzeModel';

import getGradingResult from 'src/utils/getGradingResult';

import ConnectionIssueController from '../ConnectionIssueModals/ConnectionIssueController';
import PauseExamModal from '../ConnectionIssueModals/PauseExamModal';
import CancelExamConfirmationModal from './Footers/CancelExamConfirmationModal';
import ExamCancelledModal from './Footers/ExamCancelledModal';
import FooterController from './Footers/FooterController';
import InfoCardController from './InfoCards/InfoCardController';
import StatusCardController from './StatusCards/StatusCardController';

type AnalyzingProgressModalProps = {
  isAnalyzing: boolean;
  examId: string;
  setIsAnalyzing: (isAnalyzing: boolean) => void;
  handleRetake: (position: EyesPosition) => void;
  handleCloseAnalyzingProgressModal: () => void;
  handleResetSession: () => void;
};

export default function AnalyzingProgressModal({
  isAnalyzing,
  examId,
  setIsAnalyzing,
  handleRetake,
  handleCloseAnalyzingProgressModal,
  handleResetSession,
}: AnalyzingProgressModalProps) {
  const { session, backendStatus, leftImageSubmission, rightImageSubmission } =
    useRemoteBoundStore();

  useEffect(() => {
    if (!session.orderId) {
      return;
    }
    setOrderLastStepIDB(session.orderId, ExamStepEnum.ANALYSIS);
  }, [session.orderId]);

  const analysisStartedAtRef = useRef(0);
  const warningTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const analysisTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  const warningTime = 30_000;
  const maxTime = 60_000;

  const [showPauseExamModal, setShowPauseExamModal] = useState(false);
  const [isTakingTooLong, setIsTakingTooLong] = useState(false);
  const [hasConnectionLost, setHasConnectionLost] = useState(false);
  const [gradableEyesPosition, setGradableEyesPosition] = useState<
    EyesPosition | undefined
  >(() => (session.graded ? getGradingResult(session) : undefined));

  const [showCancelExamConfirmation, setShowCancelExamConfirmation] =
    useState(false);
  const [showExamCancelled, setShowExamCancelled] = useState(false);
  const [isCancellingExam, setIsCancellingExam] = useState(false);

  const { t } = useTranslation(I18NextNS.PAGES);

  const { handleCompleteExam, handleSaveAndDilate, backToOrders, cancelExam } =
    useActionAnalyzeModel(
      handleCloseAnalyzingProgressModal,
      handleResetSession,
      session,
      setHasConnectionLost,
    );

  const getAnalysisStepProps = useCallback(
    () => ({
      step: EXAM_STEP.ANALYSIS,
      step_duration_seconds: secondsSince(analysisStartedAtRef.current),
    }),
    [],
  );

  const emitAnalysisReceived = useCallback(() => {
    const overallGradable = gradableEyesPosition === EyesPosition.BOTH;
    markAnalysisReceived();
    captureExamEvent(EXAM_EVENTS.EXAM_ANALYSIS_RECEIVED, {
      ...getAnalysisStepProps(),
      left_image_gradable: gradableEyesPosition === EyesPosition.LEFT,
      right_image_gradable: gradableEyesPosition === EyesPosition.RIGHT,
      overall_gradable: overallGradable,
      retake_required: !overallGradable,
    });
  }, [getAnalysisStepProps, gradableEyesPosition]);

  const resetProgress = useCallback(() => {
    setGradableEyesPosition(getGradingResult(session));
    setIsAnalyzing(false);
    setIsTakingTooLong(false);
    emitAnalysisReceived();
  }, [emitAnalysisReceived, session, setIsAnalyzing]);

  const { cancelSync, restartSync } = useIdbSync(resetProgress);

  const handleRequestCancelExam = () => setShowCancelExamConfirmation(true);

  const handleGoBackFromCancelExam = () => setShowCancelExamConfirmation(false);

  const handleConfirmCancelExam = async () => {
    const order_id = session.orderId;
    const exam_id = session.iid;
    setIsCancellingExam(true);
    const ok = await cancelExam();
    setIsCancellingExam(false);
    if (!ok) return;
    captureExamEvent(EXAM_EVENTS.EXAM_CANCELLED, {
      order_id,
      exam_id,
      step: EXAM_STEP.ANALYSIS,
    });
    setShowCancelExamConfirmation(false);
    setShowExamCancelled(true);
  };

  const handleBackToOrders = () => {
    setShowExamCancelled(false);
    backToOrders();
  };

  const handleConnectionRestored = async () => {
    setHasConnectionLost(false);
    setIsTakingTooLong(false);
    setIsAnalyzing(true);
    startAnalysisTimers();

    if (session.orderId) {
      void restartSync(session.orderId).catch(() => {});
    }
  };

  const emitAnalysisFailure = useCallback(
    (error: unknown) => {
      const stepProps = getAnalysisStepProps();
      captureExamEvent(EXAM_EVENTS.EXAM_ANALYSIS_FAILED, stepProps);
      captureExamException(error, stepProps);
    },
    [getAnalysisStepProps],
  );

  const clearAnalysisTimers = useCallback(() => {
    clearTimeout(warningTimeoutRef.current);
    clearTimeout(analysisTimeoutRef.current);
  }, []);

  const startAnalysisTimers = useCallback(() => {
    clearAnalysisTimers();
    analysisStartedAtRef.current = performance.now();
    warningTimeoutRef.current = setTimeout(
      () => setIsTakingTooLong(true),
      warningTime,
    );
    analysisTimeoutRef.current = setTimeout(() => {
      setHasConnectionLost(true);
      cancelSync();
      Sentry.captureMessage(
        `Exam submission timed out within ${secondsSince(analysisStartedAtRef.current)} seconds of ${maxTime} seconds for deviceDataId: ${examId}`,
      );
      emitAnalysisFailure(new Error('Exam submission timed out'));
    }, maxTime);
  }, [cancelSync, clearAnalysisTimers, emitAnalysisFailure, examId]);

  const shouldShowReachMaxRecommendedAttempts =
    leftImageSubmission >= 6 || rightImageSubmission >= 6;

  const shouldShowDilationRecommendation =
    !shouldShowReachMaxRecommendedAttempts &&
    (leftImageSubmission >= 3 || rightImageSubmission >= 3);

  useEffect(() => {
    if (backendStatus !== BackendStatusEnum.ReportEnd) {
      return;
    }

    resetProgress();
  }, [backendStatus, resetProgress]);

  useEffect(() => {
    if (!isAnalyzing) {
      return;
    }

    captureExamEvent(EXAM_EVENTS.EXAM_ANALYSIS_STARTED);
    startAnalysisTimers();

    return clearAnalysisTimers;
  }, [clearAnalysisTimers, isAnalyzing, startAnalysisTimers]);

  return (
    <CustomModal
      show
      onHide={handleCloseAnalyzingProgressModal}
      size="lg"
      data-test="analyzing-progress-modal"
      backdrop={!hasConnectionLost ? 'static' : false}
    >
      <CustomModalHeader className="p-4">
        <Stack>
          <CustomModalTitle data-test="analyzing-progress-modal-title">
            {t('newExam.imageQualityCheck')}
          </CustomModalTitle>

          <Trans
            i18nKey="newExam.imagingAttempts"
            ns="pages"
            values={{
              leftImageAttempts: leftImageSubmission,
              rightImageAttempts: rightImageSubmission,
            }}
            components={[
              <p
                className="mt-2 mb-0 text-gray-500"
                data-test="imaging-submission-attempts"
              >
                {leftImageSubmission} {rightImageSubmission}
              </p>,
            ]}
          />
        </Stack>
      </CustomModalHeader>
      <CustomModalBody className="d-flex flex-column gap-2 p-4 pt-0">
        <StatusCardController
          hasServerError={backendStatus === BackendStatusEnum.Failed}
          gradableEyesPosition={gradableEyesPosition}
          isAnalyzing={isAnalyzing}
        />

        <InfoCardController
          hasServerError={backendStatus === BackendStatusEnum.Failed}
          isMaxAttempt={shouldShowReachMaxRecommendedAttempts}
          gradableEyesPosition={gradableEyesPosition}
          isAnalyzing={isAnalyzing}
          isTakingTooLong={isTakingTooLong}
        />
      </CustomModalBody>
      <FooterController
        isAnalyzing={isAnalyzing}
        hasServerError={backendStatus === BackendStatusEnum.Failed}
        gradableEyesPosition={gradableEyesPosition}
        shouldShowDilationRecommendation={shouldShowDilationRecommendation}
        handleRetake={handleRetake}
        handleCloseAnalyzingProgressModal={handleCloseAnalyzingProgressModal}
        completeExam={handleCompleteExam}
        saveAndDilate={handleSaveAndDilate}
        onRequestCancelExam={handleRequestCancelExam}
      />

      <CancelExamConfirmationModal
        show={showCancelExamConfirmation}
        isCancelling={isCancellingExam}
        onClose={handleGoBackFromCancelExam}
        onConfirm={handleConfirmCancelExam}
      />
      <ExamCancelledModal
        show={showExamCancelled}
        onBackToOrders={handleBackToOrders}
      />

      <ConnectionIssueController
        show={hasConnectionLost && !showPauseExamModal}
        onConnectionRestored={handleConnectionRestored}
        secondaryButton={
          <Button
            variant="outline-gray-500"
            className="button-slim clear-exam-button"
            data-test="analyzing-in-progress-pause-exam"
            onClick={() => setShowPauseExamModal(true)}
          >
            {t('connectionIssue.pauseExamModal.pauseExam')}
          </Button>
        }
      />
      <PauseExamModal show={showPauseExamModal} />
    </CustomModal>
  );
}
